'use client';

import { Icons } from '@/components/icons';
import { Dialog as DialogPrimitive } from 'radix-ui';
import * as React from 'react';

import { cn } from '@/lib/cn';

const SizeMap = {
  xs: 'max-w-md w-[calc(100%-2rem)]',
  sm: 'max-w-lg w-[calc(100%-2rem)]',
  md: 'max-w-2xl w-[calc(100%-2rem)]',
  lg: 'max-w-3xl w-[calc(100%-2rem)]',
  xl: 'max-w-4xl w-[calc(100%-2rem)]',
  '2xl': 'max-w-5xl w-[calc(100%-2rem)]',
  '3xl': 'max-w-6xl w-[calc(100%-2rem)]',
};

export type DialogSize = keyof typeof SizeMap;

export type DialogProps = DialogPrimitive.DialogProps;

const DialogContext = React.createContext<{
  readonly onOpenChange: ((open: boolean) => void) | undefined;
}>({ onOpenChange: undefined });

function Dialog({ onOpenChange, ...props }: DialogPrimitive.DialogProps) {
  const value = React.useMemo(() => ({ onOpenChange }), [onOpenChange]);
  return (
    <DialogContext.Provider value={value}>
      <DialogPrimitive.Root onOpenChange={onOpenChange} {...props} />
    </DialogContext.Provider>
  );
}

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = ({
  children,
  ...props
}: DialogPrimitive.DialogPortalProps) => (
  <DialogPrimitive.Portal {...props}>
    <div className='fixed inset-0 z-50 flex items-center justify-center'>
      {children}
    </div>
  </DialogPrimitive.Portal>
);
function DialogOverlay({
  className,
  onClick,
  ref,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-row-click-ignore
      ref={ref}
      onClick={(event) => {
        event.stopPropagation();
        onClick?.(event);
      }}
      className={cn(
        // will-change-transform pins the overlay to its own compositing layer —
        // without it Chromium re-rasterizes the backdrop-filter while content
        // above it scrolls, and the blur/dim visibly flashes off (crbug).
        'fixed inset-0 z-50 transform-gpu bg-black/50 backdrop-blur-sm will-change-transform data-[state=closed]:animate-out data-[state=closed]:fade-out-0',
        className
      )}
      {...props}
    />
  );
}

interface DialogContentProps extends React.ComponentProps<
  typeof DialogPrimitive.Content
> {
  size?: DialogSize;
  overlayClassName?: string;
}

function DialogContent({
  className,
  children,
  size = 'md',
  overlayClassName,
  onClick,
  onEscapeKeyDown,
  onInteractOutside,
  ref,
  ...props
}: DialogContentProps) {
  const { onOpenChange } = React.useContext(DialogContext);

  const handleEscapeKeyDown =
    onEscapeKeyDown ??
    (onOpenChange
      ? (e: KeyboardEvent) => {
          e.stopPropagation();
          e.preventDefault();
          onOpenChange(false);
        }
      : undefined);

  const handleInteractOutside = (
    event: Parameters<NonNullable<typeof onInteractOutside>>[0]
  ) => {
    const target = event.target;
    if (target instanceof Element && target.closest('[data-sonner-toaster]')) {
      event.preventDefault();
      return;
    }
    onInteractOutside?.(event);
  };

  return (
    <DialogPortal>
      <DialogOverlay className={overlayClassName} />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed z-50 grid w-full animate-in rounded-b-lg border bg-surface p-6 shadow-lg data-[state=open]:fade-in-90 data-[state=open]:slide-in-from-bottom-10 sm:rounded-lg sm:zoom-in-90 data-[state=open]:sm:slide-in-from-bottom-0',
          SizeMap[size],
          className
        )}
        onClick={(event) => {
          event.stopPropagation();
          onClick?.(event);
        }}
        onEscapeKeyDown={handleEscapeKeyDown}
        onInteractOutside={handleInteractOutside}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn('flex items-center justify-between border-b p-4', className)}
    {...props}
  />
);

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex items-center justify-end gap-2 border-t p-4',
      className
    )}
    {...props}
  />
);

function DialogTitle({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      {...props}
      ref={ref}
      className={cn(
        'text-lg leading-none font-semibold tracking-tight',
        className
      )}
    />
  );
}

function DialogDescription({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn('text-sm text-muted-foreground', className)}
      {...props}
    />
  );
}

function DialogClose({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return (
    <DialogPrimitive.Close className={cn(className)} ref={ref} {...props} />
  );
}

function DialogCloseIcon({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return (
    <DialogPrimitive.Close
      className={cn(
        'absolute top-4 right-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground',
        className
      )}
      ref={ref}
      {...props}
    >
      <Icons.close className='size-4' />
      <span className='sr-only'>Close</span>
    </DialogPrimitive.Close>
  );
}

export type { DialogContentProps };

export {
  Dialog,
  DialogClose,
  DialogCloseIcon,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
